import {useState, forwardRef, useEffect} from "react";
import {Box, Button, TextInput, Text, Tooltip, BoxProps} from "@mantine/core";
import {isTauriAvailable, selectDirectoryTauri} from "@/common/utils/tauri.ts";
import {showErrorNotification} from "@/common/notifications.tsx";

interface DirectoryInputProps extends BoxProps {
    dirPath?: string;
    onUpdate: (updates: { dir_path?: string }) => void;
    disabled?: boolean;
    error?: string;
}

export const DirectoryInput = forwardRef<HTMLInputElement, DirectoryInputProps>(({
                                                                                     dirPath,
                                                                                     onUpdate,
                                                                                     disabled,
                                                                                     error,
                                                                                     ...other
                                                                                 }, ref) => {
    const [localDirPath, setLocalDirPath] = useState(dirPath || "");
    const [tauriAvailable] = useState(isTauriAvailable());

    useEffect(() => {
        setLocalDirPath(dirPath || "");
    }, [dirPath]);

    const handleSelectDirectory = async () => {
        if (tauriAvailable) {
            try {
                const selectedPath = await selectDirectoryTauri();
                if (selectedPath) {
                    setLocalDirPath(selectedPath);
                    onUpdate({dir_path: selectedPath});
                }
            } catch (error) {
                showErrorNotification({
                    title: 'Directory Selection Error',
                    message: `Failed to select directory: ${error instanceof Error ? error.message : String(error)}`
                });
            }
        }
    };

    return (
        <Tooltip
            label={tauriAvailable ? dirPath || "Select a directory" : `You may change the directory only when running in Neurotune Data Collector`}
        >
            <TextInput
                ref={ref}
                size="xs"
                placeholder="Select directory..."
                value={localDirPath}
                onChange={(e) => {
                    setLocalDirPath(e.currentTarget.value);
                }}
                onBlur={() => onUpdate({dir_path: localDirPath})}
                disabled={disabled || !tauriAvailable}
                error={error}
                styles={{
                    root: {flex: 1},
                    section: {paddingRight: "1.6rem"},
                }}
                rightSection={
                    <Box style={{display: "flex"}}>
                        <Button
                            style={{padding: "0.25em"}}
                            size="compact-xs"
                            onClick={handleSelectDirectory}
                            disabled={disabled || !tauriAvailable}
                        >
                            <Text size={"0.7rem"} fw={"normal"}>Browse</Text>
                        </Button>
                    </Box>
                }
                {...other}
            />
        </Tooltip>
    );
});
